/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { openGlobalModal, IModalData, IDialogOptions } from 'app/shared/elements/ess-global-modal';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { AuthService } from 'app/um/services/auth-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import * as UM from 'app/um/models/essette-api';

export interface IClaimSystemLogModelData {
    auth: UM.Auth;
}

@autoinject()
export class ClaimSystemLog extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected authId: string | null = null;
    protected eagleTransactions: Array<UM.EagleTransaction> = [];

    private sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 25;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        ...rest: any[]) {
        super(...rest);
    }

    protected async activate(model: IClaimSystemLogModelData): Promise<void> {
        if(!model.auth.id) return;

        this.authId = model.auth.id;
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        await this.reload();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadEagleTransactions();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadEagleTransactions(): Promise<void> {
        if(!this.authId) return;

        // restore default sort if no other sorts specified
        if(!this.sorting.entries.length) {
            this.sorting.entries = [
                {
                    reference: 'metadata.created.timestamp',
                    direction: SortDirection.Descending
                }
            ];
        }

        const includes = [ 'status', 'metadata.created.byUser' ];

        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0
        };

        try {
            this.state.beginLoad();

            const response = await this.service.loadEagleTransactionsByAuth(this.authId, options, includes);
            this.eagleTransactions = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}

/**
 * Opens the claim system log modal with the global dialog
 * @param auth The auth information to use
 */
export function openClaimSystemLog(auth: UM.Auth): void {
    if(!auth || !auth.id) {
        throw new Error('Auth is missing or invalid');
    }

    const dialogOptions: IDialogOptions = {
        width: '10',
        height: '11',
        helpTopic: 'um.auth.claim-system-log',
        title: {
            key: 'um:routes.auth.claim-system-log.$title',
            replace: { number: auth.number }
        },
        print: true,
        popout: {
            route: 'um.auth.claim-system-log',
            params: { authId: auth.id }
        }
    };

    const modelData: IClaimSystemLogModelData = {
        auth: auth
    };

    const modalData: IModalData = {
        componentId: 'app/um/components/claim-system-log/claim-system-log',
        modelData: modelData,
        dialogOptions: dialogOptions
    };

    openGlobalModal(modalData);
}
